/**
 * Typed API client.
 *
 * Wraps `openapi-fetch` against the schema generated by
 * `npm run openapi:gen` (which reads ../kg/build/schema.yml emitted by
 * `manage.py spectacular`). Until that file exists, the generic `paths`
 * type is `unknown` and calls fall back to plain JSON without typing.
 *
 * Auth: the backend supports Token and ApiKey auth. We send the token
 * stored in localStorage (set during login) as `Authorization: Token …`.
 *
 * Multi-tenancy: when a workspace is selected the Studio also sends
 * `X-Tenant-Schema: <slug>` so the BE TenantHeaderMiddleware pins the
 * Postgres schema regardless of how the FE was opened (the dev Vite
 * proxy runs on localhost which would otherwise resolve to `public`).
 */
import createClient, { type Middleware } from "openapi-fetch";
import type { paths } from "./schema";

const BASE = import.meta.env.VITE_API_BASE_URL || "/api/v1";

function tenantSlug(): string | null {
  // Stored by Layout / Workspaces page when the user picks a workspace.
  return localStorage.getItem("ckos.tenantSchema");
}

/** Appends ?workspace=<id> to a path so the BE middleware can resolve the
 *  tenant schema even when the X-Tenant-Schema header is stale or absent.
 *  POST/multipart endpoints don't carry ?workspace= by default, which
 *  causes them to land on the public schema.
 */
function withWorkspace(path: string): string {
  const id = localStorage.getItem("ckos.workspaceId");
  if (!id) return path;
  const sep = path.includes("?") ? "&" : "?";
  return `${path}${sep}workspace=${id}`;
}

const authMiddleware: Middleware = {
  async onRequest({ request }) {
    const token = localStorage.getItem("ckos.token");
    if (token) request.headers.set("Authorization", `Token ${token}`);
    const slug = tenantSlug();
    if (slug) request.headers.set("X-Tenant-Schema", slug);
    return request;
  },
};

export const api = createClient<paths>({ baseUrl: BASE });
api.use(authMiddleware);

/**
 * Extracts a human-readable message from a non-2xx Response.
 * Prefers the `detail` field from a JSON body (Django/DRF convention),
 * then `message`, then falls back to the status text.
 * For 503 "tenant_migration_pending" responses the code is embedded
 * so callers can style them differently.
 */
async function _apiError(r: Response): Promise<Error> {
  let detail = `${r.status} ${r.statusText}`;
  let body: Record<string, unknown> | undefined;
  try {
    const ct = r.headers.get("content-type") || "";
    if (ct.includes("application/json")) {
      body = await r.json() as Record<string, unknown>;
      const msg = (body?.detail ?? body?.message ?? body?.error ?? "") as string;
      if (msg) detail = msg;
    } else {
      const text = (await r.text()).trim();
      if (text) detail = `${r.status}: ${text.slice(0, 200)}`;
    }
  } catch { /* ignore parse failures */ }
  const err = new Error(detail) as Error & { status?: number; body?: Record<string, unknown> };
  err.status = r.status;
  if (body) err.body = body;
  return err;
}

/** Public helper for endpoints that use direct fetch instead of rawFetch. */
export async function parseApiError(r: Response): Promise<Error> {
  return _apiError(r);
}

/** Workspace-aware fetch for callers that need the untouched Response
 * (streaming downloads, response headers, or non-JSON payloads). */
export async function rawResponse(path: string, init?: RequestInit): Promise<Response> {
  const token = localStorage.getItem("ckos.token");
  const headers = new Headers(init?.headers);
  if (token) headers.set("Authorization", `Token ${token}`);
  const slug = tenantSlug();
  if (slug) headers.set("X-Tenant-Schema", slug);
  const response = await fetch(`${BASE}${withWorkspace(path)}`, { ...init, headers });
  if (!response.ok) throw await _apiError(response);
  return response;
}

/** Untyped escape hatch for endpoints not yet in the schema.
 *  `responseType` defaults to "json"; pass "text" to read text/turtle, CSV, etc.
 *  `scopeToWorkspace` defaults to `true` (append `?workspace=<id>`); pass
 *  `false` for cross-workspace admin views (e.g. an audit trail spanning
 *  every workspace in the tenant) that must not be filtered down to the
 *  currently selected workspace.
 */
export async function rawFetch<T = unknown>(
  path: string,
  init?: RequestInit,
  responseType: "json" | "text" = "json",
  scopeToWorkspace = true,
): Promise<T> {
  const token = localStorage.getItem("ckos.token");
  const headers = new Headers(init?.headers);
  if (!headers.has("Content-Type")) headers.set("Content-Type", "application/json");
  if (token) headers.set("Authorization", `Token ${token}`);
  const slug = tenantSlug();
  if (slug) headers.set("X-Tenant-Schema", slug);
  const r = await fetch(`${BASE}${scopeToWorkspace ? withWorkspace(path) : path}`, { ...init, headers });
  if (!r.ok) {
    throw await _apiError(r);
  }
  if (r.status === 204) return undefined as T;
  if (responseType === "text") return (await r.text()) as unknown as T;
  return (await r.json()) as T;
}

/** Origin of the backend API — i.e. BASE with the `/api/v<n>` suffix removed.
 *  Root-level endpoints that live OUTSIDE the versioned API surface (e.g. the
 *  Celery worker-health probe at `/health/worker`) hang off this origin, not
 *  off BASE. In dev this resolves to "" (same-origin, proxied by Vite); in
 *  prod it resolves to the API host (e.g. https://api.ontoligent.io). */
export const API_ORIGIN = BASE.replace(/\/api\/v\d+\/?$/, "");

/** GET a root-level (non-versioned) API endpoint such as `/health/worker`.
 *  Sends the same auth + tenant headers as `rawFetch` but never appends
 *  `?workspace=` and never prefixes the versioned BASE. Never throws on a
 *  non-2xx that still carries JSON — the health probe always returns 200. */
export async function rawRootFetch<T = unknown>(path: string): Promise<T> {
  const token = localStorage.getItem("ckos.token");
  const headers = new Headers();
  if (token) headers.set("Authorization", `Token ${token}`);
  const slug = tenantSlug();
  if (slug) headers.set("X-Tenant-Schema", slug);
  const r = await fetch(`${API_ORIGIN}${path}`, { headers });
  if (!r.ok) throw await _apiError(r);
  return (await r.json()) as T;
}

/**
 * Fetch an arbitrary blob (binary or text) and trigger a browser download.
 *
 * Used by ontology exporters which return JSON-LD, Turtle, OWL/XML, Cypher,
 * CSV zip, or graph-json. Honours the same Token + X-Tenant-Schema headers
 * as `rawFetch` so the BE pins the correct Postgres schema.
 *
 * The filename is derived from the response's Content-Disposition header
 * when present, otherwise from the caller-supplied `fallbackName`.
 */
export async function downloadBlob(path: string, fallbackName: string): Promise<void> {
  const token = localStorage.getItem("ckos.token");
  const headers = new Headers();
  if (token) headers.set("Authorization", `Token ${token}`);
  const slug = tenantSlug();
  if (slug) headers.set("X-Tenant-Schema", slug);
  const r = await fetch(`${BASE}${path}`, { headers });
  if (!r.ok) {
    throw await _apiError(r);
  }
  const cd = r.headers.get("content-disposition") || "";
  const m = /filename="?([^"]+)"?/.exec(cd);
  const name = m ? m[1] : fallbackName;
  const blob = await r.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

/**
 * POST a multipart/form-data body. Browser sets the boundary, so we
 * intentionally do NOT set Content-Type. Returns parsed JSON or throws
 * with a trimmed body excerpt on non-2xx (which is also helpful when
 * a misconfigured BASE makes the request fall through to the SPA host
 * and we get HTML back instead of a 4xx).
 */
export async function rawMultipart<T = unknown>(path: string, form: FormData): Promise<T> {
  const token = localStorage.getItem("ckos.token");
  const headers = new Headers();
  if (token) headers.set("Authorization", `Token ${token}`);
  const slug = tenantSlug();
  if (slug) headers.set("X-Tenant-Schema", slug);
  const r = await fetch(`${BASE}${withWorkspace(path)}`, { method: "POST", headers, body: form });
  const ct = r.headers.get("content-type") || "";
  if (!r.ok) {
    throw await _apiError(r);
  }
  if (r.status === 204) return undefined as T;
  if (!ct.includes("application/json")) {
    // SPA fallback returned HTML, or some proxy stripped JSON — surface a real error
    const text = await r.text();
    throw new Error(`Expected JSON, got ${ct || "unknown"} (${text.slice(0, 120)}…). Check VITE_API_BASE_URL.`);
  }
  return (await r.json()) as T;
}
